import React, { useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollProgress from './components/ScrollProgress';
import Home from './pages/Home';
import About from './pages/About';
import Vision from './pages/Vision';
import Services from './pages/Services';
import Contact from './pages/Contact';
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import { Link } from 'react-router-dom';

// Lazy load the Our Work page and service landing pages
const OurWork = lazy(() => import('./pages/our-work'));
const Printing = lazy(() => import('./pages/services/Printing'));
const CompanyRegistration = lazy(() => import('./pages/services/CompanyRegistration'));
const Branding = lazy(() => import('./pages/services/Branding'));
const ApparelPrinting = lazy(() => import('./pages/services/ApparelPrinting'));
const CorporateStationery = lazy(() => import('./pages/services/CorporateStationery'));
const VehicleWraps = lazy(() => import('./pages/services/VehicleWraps'));
const DigitalServices = lazy(() => import('./pages/services/DigitalServices'));
const WebServices = lazy(() => import('./pages/services/WebServices'));
const AppDevelopment = lazy(() => import('./pages/services/AppDevelopment'));
const Automation = lazy(() => import('./pages/services/Automation'));
const Quote = lazy(() => import('./pages/Quote'));

// Loading component
function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-primary-950">
      <div className="flex flex-col items-center space-y-4">
        <div className="w-12 h-12 border-4 border-azure-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-gray-400 text-lg">Loading...</p>
      </div>
    </div>
  );
}

// 404 — keeps unknown URLs from rendering a blank page
function NotFound() {
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center bg-primary-950 text-center px-4">
      <p className="font-display text-7xl font-bold text-azure-500">404</p>
      <h1 className="mt-4 font-display text-2xl font-bold text-white">Page not found</h1>
      <p className="mt-2 text-gray-400">The page you're looking for doesn't exist or has moved.</p>
      <Link to="/" className="btn-primary mt-8">Back to Home</Link>
    </div>
  );
}

// ScrollToTop component
function ScrollToTop() {
  const location = useLocation();

  useEffect(() => {
    // Instant jump — smooth-scrolling on route change makes navigation feel laggy
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [location]);

  return null;
}

function App() {
  // Initialize Google Analytics tracking
  useGoogleAnalytics();

  return (
    <div className="grain min-h-screen flex flex-col">
      <ScrollToTop />
      <ScrollProgress />
      <Navbar />
      <main className="flex-grow">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/vision" element={<Vision />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/printing" element={<Printing />} />
            <Route path="/services/company-registration" element={<CompanyRegistration />} />
            <Route path="/services/branding" element={<Branding />} />
            <Route path="/services/apparel-printing" element={<ApparelPrinting />} />
            <Route path="/services/corporate-stationery" element={<CorporateStationery />} />
            <Route path="/services/vehicle-wraps" element={<VehicleWraps />} />
            <Route path="/services/digital-services" element={<DigitalServices />} />
            <Route path="/services/web-services" element={<WebServices />} />
            <Route path="/services/app-development" element={<AppDevelopment />} />
            <Route path="/services/automation" element={<Automation />} />
            <Route path="/quote" element={<Quote />} />
            <Route path="/our-work" element={<OurWork />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}

export default App;